import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import EmblaCarousel from '@theme/embla-carousel';
import Autoplay from '@theme/embla-carousel-autoplay';
import { bindGrabCursor } from '@theme/be-embla-grab-cursor';

/**
 * Generic Embla-powered carousel, the reusable successor to the bespoke
 * wrappers in be-home-hero-carousel.js / vineyard-carousel.js. Loops
 * seamlessly in both directions; Embla falls back to a non-looping engine
 * automatically when there aren't enough slides to keep the viewport covered.
 *
 * Markup contract:
 * - `ref="viewport"` (required) — the overflow-hidden element; its first
 *   child is the slide track. Slide spacing must be a per-slide end margin,
 *   not flex `gap` (Embla sizes the loop seam from the last slide's end
 *   margin).
 * - `ref="alignProbe"` (optional) — a zero-height element whose inline-size
 *   is the resting start offset (e.g. a page-margin CSS variable). Measured
 *   on every Embla (re)init, so responsive/calc() values just work. An
 *   explicit `align` attribute ('center' | 'end') overrides the probe;
 *   otherwise slides align 'start'.
 * - `mobile-align="center"` attribute (optional) — centers the active slide
 *   below 750px, overriding the probe/`align` value there.
 * - `drag-free` attribute (optional) — free-scroll dragging: momentum
 *   settles wherever it lands instead of snapping to a slide. Arrow
 *   buttons still page snap-to-snap.
 * - `autoplay` attribute (optional) — auto-rotate interval in seconds;
 *   ignored when the visitor prefers reduced motion.
 *
 * Arrow buttons (e.g. snippets/slideshow-arrow.liquid) declare
 * `on:click="/previous"` / `on:click="/next"`, which the Component framework
 * delegates to the methods below.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The overflow-hidden element Embla measures.
 * @property {HTMLElement} [alignProbe] - Optional probe sized to the resting start offset.
 *
 * @extends {Component<Refs>}
 */
class BeEmblaCarousel extends Component {
  requiredRefs = ['viewport'];

  /** @type {ReturnType<typeof EmblaCarousel> | undefined} */
  #embla;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  updatedCallback() {
    super.updatedCallback();

    // Section Rendering API / editor morphs can change slides or settings —
    // rebuild so Embla re-reads both the DOM and this element's attributes.
    this.#destroy();
    this.#init();
  }

  /**
   * Scrolls to the next slide, wrapping from the last to the first.
   */
  next() {
    this.#embla?.scrollNext();
  }

  /**
   * Scrolls to the previous slide, wrapping from the first to the last.
   */
  previous() {
    this.#embla?.scrollPrev();
  }

  /**
   * The auto-rotate interval in milliseconds, or 0 when disabled.
   * @returns {number}
   */
  get autoplayDelay() {
    const seconds = parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(seconds) ? 0 : seconds * 1000;
  }

  #init() {
    const { viewport, alignProbe } = this.refs;
    const align = this.getAttribute('align');
    const mobileCenter = this.getAttribute('mobile-align') === 'center';
    const plugins = [];

    if (this.autoplayDelay > 0 && !prefersReducedMotion()) {
      plugins.push(
        Autoplay({
          delay: this.autoplayDelay,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
          stopOnFocusIn: true,
        })
      );
    }

    this.#embla = EmblaCarousel(
      viewport,
      {
        loop: true,
        // By default Embla clamps a drag gesture to the adjacent snap, which
        // reads as "snapping back" when you've dragged past a slide.
        // skipSnaps lets momentum carry to whichever snap the flick reaches,
        // and the longer duration softens the settle.
        skipSnaps: true,
        dragFree: this.hasAttribute('drag-free'),
        duration: 30,
        direction: document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr',
        align:
          align === 'center' || align === 'end'
            ? align
            : alignProbe
              ? (viewSize, snapSize) =>
                  mobileCenter && window.matchMedia('(max-width: 749px)').matches
                    ? (viewSize - snapSize) / 2
                    : alignProbe.getBoundingClientRect().width
              : 'start',
      },
      plugins
    );

    bindGrabCursor(this.#embla);
  }

  #destroy() {
    this.#embla?.destroy();
    this.#embla = undefined;
  }
}

if (!customElements.get('be-embla-carousel')) {
  customElements.define('be-embla-carousel', BeEmblaCarousel);
}
